import { Bitmap, BitmapData, Point, Rectangle, Sprite } from "../runtime";
import { SPR_generatorBroken } from "./assets";
import {
  BG_VERTICAL_LAYOUT,
  FORTRESS_SPRITES,
  ZONE_BACKGROUNDS,
  ZONE_SHEETS,
} from "./data/zones";
import type {
  Barrier,
  Block,
  ItemPickup,
  Level,
  Obstacle,
  TriggerPass,
} from "./level";

/** Frame size the level is drawn into, and the level's size in background tiles. */
export interface BgLevelData {
  levelX: number;
  levelY: number;
  bgX: number;
  bgY: number;
  bgTotal: number;
}

/** Smoke puff emitted by a wrecked generator; `Game` drains the queue each frame. */
export interface SmokePuff {
  xx: number;
  yy: number;
  /** emission angle in degrees */
  a: number;
}

interface WreckedGenerator {
  xx: number;
  yy: number;
  life: number;
  smokeCounter: number;
}

export class BgGenerator extends Sprite {
  readonly levelData: BgLevelData = {
    levelX: 0,
    levelY: 0,
    bgX: 0,
    bgY: 0,
    bgTotal: 0,
  };
  readonly smokeArray: SmokePuff[] = [];
  obsArray: Obstacle[][];
  triggerPass: TriggerPass[];
  barriers: Barrier[];
  blocks: Block[];
  itemPickup: ItemPickup[];
  startPoint: Point;
  bossData: Level["bossData"];
  raining = 0;
  private readonly tileSize = 512;
  private readonly bgArray: readonly (number | null)[];
  private readonly generatorArray: WreckedGenerator[] = [];
  private readonly generatorSpr: BitmapData = new SPR_generatorBroken(0, 0);
  private readonly stageBitmapData: BitmapData;
  private readonly stageRect: Rectangle;
  private readonly objectSheet: BitmapData;
  private readonly bgTop: BitmapData;
  private readonly bgMiddle: BitmapData;
  private readonly bgBottom: BitmapData;
  private readonly blockSheetA: BitmapData;
  private readonly blockSheetB: BitmapData;
  private readonly bgXTotal: number;
  private readonly bgMiddleRows: number;
  private readonly bgOffsetY: number;
  private readonly fortressStage: number = 0;
  private readonly fortSpr?: BitmapData;
  private readonly fortRect?: Rectangle;
  private readonly fortMult: number = 0;
  private readonly fortOffX: number = 0;
  private readonly fortOffY: number = 0;
  private readonly blockDrawPos = new Point(0, 0);
  private readonly blockRect = new Rectangle(0, 0, 64, 64);
  private readonly tileDrawPos = new Point(0, 0);
  private readonly tileRect = new Rectangle(0, 0, 512, 512);
  private readonly fortressDrawPos = new Point(0, 0);
  private readonly backgroundDrawPos = new Point(0, 0);
  private readonly backgroundRect = new Rectangle(0, 0, 512, 512);
  private readonly generatorDrawPos = new Point(0, 0);
  private readonly generatorRect = new Rectangle(0, 0, 96, 96);

  constructor(levelWidth: number, levelHeight: number, level: Level) {
    super();
    this.obsArray = level.obstacleArray;
    this.bgArray = level.bgArray;
    this.levelData.levelX = levelWidth;
    this.levelData.levelY = levelHeight;
    this.levelData.bgX = level.bgX;
    this.levelData.bgY = level.bgY;
    this.triggerPass = level.triggerPass;
    this.barriers = level.barriers;
    this.startPoint = level.startPoint;
    this.blocks = level.blocks;
    this.itemPickup = level.itemPickup;
    this.bossData = level.bossData;
    this.raining = level.raining;

    const zone = ZONE_BACKGROUNDS[ZONE_IDS[level.zone]];
    this.bgTop = new zone.top(0, 0);
    this.bgMiddle = new zone.middle(0, 0);
    this.bgBottom = new zone.bottom(0, 0);
    const sheets = ZONE_SHEETS[zone.type];
    this.objectSheet = new sheets.obs(0, 0);
    this.blockSheetA = new sheets.blockA(0, 0);
    this.blockSheetB = new sheets.blockB(0, 0);

    this.fortressStage = level.fortressStage;
    if (this.fortressStage > 0) {
      const fort = FORTRESS_SPRITES[this.fortressStage];
      this.fortSpr = new fort.spr(0, 0);
      this.fortMult = fort.mult;
      this.fortRect = new Rectangle(0, 0, fort.width, fort.height);
      this.fortOffX =
        (this.levelData.bgX * 512 * fort.mult - fort.width * 0.5) * 0.5;
      this.fortOffY =
        (this.levelData.bgY * 512 - 490) * fort.mult - fort.height;
    }

    this.levelData.bgTotal = this.levelData.bgX * this.levelData.bgY;
    this.stageRect = new Rectangle(
      0,
      0,
      this.levelData.levelX,
      this.levelData.levelY,
    );
    this.stageBitmapData = new BitmapData(
      this.levelData.levelX,
      this.levelData.levelY,
      true,
      13816575,
    );
    this.addChild(new Bitmap(this.stageBitmapData));
    this.bgXTotal = Math.floor(this.levelData.bgX * 0.5) + 1;
    const vertical = BG_VERTICAL_LAYOUT[this.levelData.bgY];
    this.bgMiddleRows = vertical?.middleRows ?? 0;
    this.bgOffsetY = vertical?.offsetY ?? -90;
  }

  update(offsetX = 0, offsetY = 0): void {
    this.stageBitmapData.lock();
    this.stageBitmapData.fillRect(this.stageRect, 16777215);
    this.drawBackground(offsetX, offsetY);
    if (this.fortressStage > 0) {
      this.drawFortress(offsetX, offsetY);
    }
    for (const block of this.blocks) {
      const drawPos = this.blockDrawPos;
      drawPos.x = block.xx + offsetX;
      drawPos.y = block.yy + offsetY;
      const tileRect = this.blockRect;
      const sheet = block.id === 0 ? this.blockSheetA : this.blockSheetB;
      this.stageBitmapData.copyPixels(
        sheet,
        tileRect,
        drawPos,
        null,
        null,
        true,
      );
    }
    for (let n = 0; n < this.levelData.bgTotal; n++) {
      const tile = this.bgArray[n];
      if (tile === null) continue;
      const drawPos = this.tileDrawPos;
      drawPos.x = ((n % this.levelData.bgX) | 0) * this.tileSize + offsetX;
      drawPos.y = ((n / this.levelData.bgX) | 0) * this.tileSize + offsetY;
      const tileRect = this.tileRect;
      tileRect.x = ((tile % 6) | 0) * this.tileSize;
      tileRect.y = ((tile / 6) | 0) * this.tileSize;
      this.stageBitmapData.copyPixels(
        this.objectSheet,
        tileRect,
        drawPos,
        null,
        null,
        true,
      );
    }
    this.drawGenerators(offsetX, offsetY);
    this.stageBitmapData.unlock();
  }

  createGeneratorDamaged(x = 0, y = 0): void {
    this.generatorArray.push({
      xx: x - 48,
      yy: y - 48,
      life: 0,
      smokeCounter: 0,
    });
  }

  private drawFortress(offsetX: number, offsetY: number): void {
    const drawPos = this.fortressDrawPos;
    drawPos.x = offsetX * this.fortMult + this.fortOffX;
    drawPos.y = offsetY * this.fortMult + this.fortOffY;
    this.stageBitmapData.copyPixels(
      this.fortSpr!,
      this.fortRect!,
      drawPos,
      null,
      null,
      true,
    );
  }

  private drawBackground(offsetX: number, offsetY: number): void {
    const drawPos = this.backgroundDrawPos;
    const tileRect = this.backgroundRect;
    for (let bgTX = 0; bgTX < this.bgXTotal; bgTX++) {
      drawPos.x = offsetX * 0.32 - 30 + bgTX * 512;
      drawPos.y = offsetY * 0.32 - 30 + this.bgOffsetY;
      this.stageBitmapData.copyPixels(
        this.bgTop,
        tileRect,
        drawPos,
        null,
        null,
        true,
      );
    }
    for (let bgMY = 0; bgMY < this.bgMiddleRows; bgMY++) {
      for (let bgMX = 0; bgMX < this.bgXTotal; bgMX++) {
        drawPos.x = offsetX * 0.32 - 30 + bgMX * 512;
        drawPos.y = offsetY * 0.32 - 30 + bgMY * 512 + 512 + this.bgOffsetY;
        this.stageBitmapData.copyPixels(
          this.bgMiddle,
          tileRect,
          drawPos,
          null,
          null,
          true,
        );
      }
    }
    for (let bgBX = 0; bgBX < this.bgXTotal; bgBX++) {
      drawPos.x = offsetX * 0.32 - 30 + bgBX * 512;
      drawPos.y =
        offsetY * 0.32 - 30 + this.bgMiddleRows * 512 + 512 + this.bgOffsetY;
      this.stageBitmapData.copyPixels(
        this.bgBottom,
        tileRect,
        drawPos,
        null,
        null,
        true,
      );
    }
  }

  private drawGenerators(offsetX: number, offsetY: number): void {
    for (const gen of this.generatorArray) {
      const drawPos = this.generatorDrawPos;
      drawPos.x = gen.xx + offsetX;
      drawPos.y = gen.yy + offsetY;
      const tileRect = this.generatorRect;
      this.stageBitmapData.copyPixels(
        this.generatorSpr,
        tileRect,
        drawPos,
        null,
        null,
        true,
      );
      if (gen.life >= 130) {
        continue;
      }
      ++gen.life;
      ++gen.smokeCounter;
      if (gen.smokeCounter === 9) {
        gen.smokeCounter = 0;
        // puffs are emitted from the wreck's centre, angles are 180 degrees off the visual spread
        this.smokeArray.push(
          { xx: gen.xx + 48, yy: gen.yy - 10 + 48, a: 90 + 180 },
          { xx: gen.xx - 17 + 48, yy: gen.yy + 2 + 48, a: 20 + 180 },
          { xx: gen.xx + 17 + 48, yy: gen.yy + 2 + 48, a: 160 + 180 },
        );
      }
    }
  }
}

const ZONE_IDS: Readonly<Record<Level["zone"], number>> = {
  day: 0,
  sunset: 1,
  night: 2,
  "city-day": 3,
  underground: 4,
  "city-night": 5,
  fortress: 6,
  vr: 10,
};
